import { ChangeDetectionStrategy, Component, ViewEncapsulation, booleanAttribute, computed, input } from '@angular/core';
import { ButtonSize, ButtonStyle, ButtonType } from './button';

/**
 * ZagAnchor Component
 *
 * An anchor/link component styled as a button with support for multiple styles,
 * types, sizes, icons, and disabled state. Uses content projection for flexible
 * icon and text layouts.
 *
 * @example Basic usage
 * ```html
 * <a zagButton href="/dashboard">Go to Dashboard</a>
 * <a zagButton="outlined" zagType="secondary" href="/settings">Settings</a>
 * ```
 *
 * @example Style variants
 * ```html
 * <a zagButton href="/">Basic (default)</a>
 * <a zagButton="outlined" href="/">Outlined</a>
 * <a zagButton="elevated" href="/">Elevated</a>
 * <a zagButton="tonal" href="/">Tonal</a>
 * ```
 *
 * @example With icons (using ZagIcon directive)
 * ```html
 * <a zagButton="outlined" href="/back">
 *   <span zagIcon="fa-arrow-left"></span>
 *   Back
 * </a>
 * ```
 *
 * @example Disabled anchor
 * ```html
 * <a zagButton href="/restricted" disabled>Restricted Area</a>
 * ```
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'a[zagButton]',
  templateUrl: './button.html',
  styleUrls: ['./button.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.zag-button]': 'true',
    // Style variants
    '[class.zag-button--basic]': 'buttonStyle() === "basic"',
    '[class.zag-button--outlined]': 'buttonStyle() === "outlined"',
    '[class.zag-button--elevated]': 'buttonStyle() === "elevated"',
    '[class.zag-button--tonal]': 'buttonStyle() === "tonal"',
    // Size variants
    '[class.zag-button--sm]': 'zagSize() === "sm"',
    '[class.zag-button--md]': 'zagSize() === "md"',
    '[class.zag-button--lg]': 'zagSize() === "lg"',
    // Type (color) variants
    '[class.zag-button--primary]': 'zagType() === "primary"',
    '[class.zag-button--secondary]': 'zagType() === "secondary"',
    '[class.zag-button--success]': 'zagType() === "success"',
    '[class.zag-button--warning]': 'zagType() === "warning"',
    '[class.zag-button--error]': 'zagType() === "error"',
    '[class.zag-button--info]': 'zagType() === "info"',
    // State variants
    '[class.zag-button--disabled]': 'disabled()',
    '[class.zag-button--collapse]': 'zagCollapse()',
    '[attr.aria-disabled]': 'ariaDisabled()',
    '[attr.tabindex]': 'tabIndex()',
  },
})
export class ZagAnchor {
  /**
   * Button style variant (appearance)
   * - basic: Solid fill, no shadow (default)
   * - outlined: Transparent background, colored border
   * - elevated: Solid fill with box-shadow
   * - tonal: Lighter tint background
   */
  readonly zagButton = input<ButtonStyle | ''>('');

  /**
   * Computed button style - defaults to 'basic' when empty or not provided
   */
  readonly buttonStyle = computed<ButtonStyle>(() => {
    const style = this.zagButton();
    return style === '' ? 'basic' : style;
  });

  /**
   * Button type/variant for color styling
   */
  readonly zagType = input<ButtonType>('primary');

  /**
   * Button size variant
   */
  readonly zagSize = input<ButtonSize>('md');

  /**
   * Disabled state - sets aria-disabled, tabindex=-1, and pointer-events: none
   */
  readonly disabled = input(false, { transform: booleanAttribute });

  /**
   * Responsive collapse - hides text on small screens, shows only icon
   */
  readonly zagCollapse = input(false, { transform: booleanAttribute });

  readonly zagLoading = input(false, { transform: booleanAttribute });

  /**
   * Computed aria-disabled attribute value
   */
  readonly ariaDisabled = computed(() => (this.disabled() ? 'true' : null));

  /**
   * Computed tabindex attribute value - -1 when disabled to remove from tab order
   */
  readonly tabIndex = computed(() => (this.disabled() ? -1 : null));
}
